// Strategies list + create. Phase 6 (2/2).
//
// Lists every strategy row in the DB with at-a-glance status / NAV /
// growth / position id. Row-level actions: pause/resume/close (via the
// Phase 5 lifecycle endpoints). A "+ New strategy" button opens a modal
// with a full create form.
//
// We deliberately do NOT show the operations panel here — that lives on
// Overview, which is per-strategy. This page is the strategy *roster*.
import { useState as useReactState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Pause, Play, Plus, Square } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SectionHeader } from '../components/ui/section-header';
import { StatBadge } from '../components/ui/stat-badge';
import { useCurrentUser, useStrategies, useWallets } from '../hooks/useApi';
import { deploymentsApi, strategiesApi, type StrategyDetail } from '../lib/api';
import { cn, formatCurrency, formatDateTime } from '../lib/utils';

export default function Strategies() {
  const { data: strategies, isLoading, error } = useStrategies();
  const { data: me } = useCurrentUser();
  const [openCreate, setOpenCreate] = useReactState(false);
  const canWrite = !me || me.role === 'admin' || me.role === 'fund_manager';

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (error) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-negative">
        Couldn&apos;t load strategies: {String(error)}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl">
      <SectionHeader
        title="Strategies"
        subtitle="One row per LP+hedge pairing. Click a row for details."
        size="lg"
        actions={
          <Button
            size="sm"
            onClick={() => setOpenCreate(true)}
            disabled={!canWrite}
          >
            <Plus className="h-3.5 w-3.5" />
            New strategy
          </Button>
        }
      />

      <div className="rounded-lg border border-border bg-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                Slug / Name
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                Status
              </th>
              <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                Deployed
              </th>
              <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                Range
              </th>
              <th
                className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3"
                title="continuous: drift-triggered rebalance (legacy). static: opens once at epoch start, frozen until next redeploy."
              >
                Hedge
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                NFT
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                Inception
              </th>
              <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {(strategies ?? []).map((s) => (
              <StrategyRow key={s.id} strategy={s} canWrite={canWrite} />
            ))}
            {(strategies ?? []).length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No strategies yet. Click &ldquo;New strategy&rdquo; to add one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openCreate && <CreateStrategyModal onClose={() => setOpenCreate(false)} />}
    </div>
  );
}

function StrategyRow({
  strategy,
  canWrite,
}: {
  strategy: StrategyDetail;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useReactState<string | null>(null);

  async function lifecycle(label: string, fn: () => Promise<unknown>) {
    setBusy(label);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['strategies'] });
      await qc.invalidateQueries({ queryKey: ['state'] });
    } catch (err) {
      alert(`${label} failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  const statusBadge = (() => {
    if (strategy.status === 'active')
      return <StatBadge label="Active" variant="positive" dot />;
    if (strategy.status === 'paused')
      return <StatBadge label="Paused" variant="pending" dot />;
    if (strategy.status === 'closed')
      return <StatBadge label="Closed" variant="neutral" dot />;
    return <StatBadge label={strategy.status} variant="neutral" dot />;
  })();

  return (
    <tr className="border-t border-border hover:bg-accent/30 transition-colors">
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{strategy.displayName}</div>
        <div className="text-xs text-muted-foreground tabular-nums">{strategy.slug}</div>
      </td>
      <td className="px-4 py-3">{statusBadge}</td>
      <td className="px-4 py-3 tabular-nums text-right">
        {formatCurrency(strategy.deployedCapitalUsd)}
      </td>
      <td className="px-4 py-3 tabular-nums text-right text-muted-foreground">
        ±{strategy.rangePct}%
      </td>
      <td className="px-4 py-3">
        {/* Hedge-model column: 'static' gets the colored badge so it's
            visually scannable in the table; 'continuous' is the
            legacy default and uses muted text so it doesn't compete
            for attention. */}
        {strategy.hedgeModel === 'static' ? (
          <span
            className="px-1.5 py-0.5 text-[10px] rounded-full border border-primary/30 bg-primary/10 text-primary"
            title="Hedge opens once at epoch start, frozen until next redeploy."
          >
            static
          </span>
        ) : (
          <span
            className="text-xs text-muted-foreground"
            title="Hedge tracks LP continuously, triggered by drift threshold."
          >
            continuous
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">
        {strategy.positionNftId ?? '—'}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {formatDateTime(strategy.inceptionTs)}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex gap-1">
          {strategy.status === 'active' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => lifecycle('Pause', () => strategiesApi.pause(strategy.id))}
              disabled={!canWrite || busy !== null}
              title="Pause: bot keeps observing but stops auto-actions"
            >
              <Pause className="h-3.5 w-3.5" />
              {busy === 'Pause' ? '…' : 'Pause'}
            </Button>
          )}
          {strategy.status === 'paused' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => lifecycle('Resume', () => strategiesApi.resume(strategy.id))}
              disabled={!canWrite || busy !== null}
            >
              <Play className="h-3.5 w-3.5" />
              {busy === 'Resume' ? '…' : 'Resume'}
            </Button>
          )}
          {strategy.status !== 'closed' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (
                  confirm(
                    `Close strategy "${strategy.displayName}"?\n\nThis marks the strategy as closed. The bot will stop iterating on it. Use this only after you've manually unwound the position (or you intend to leave it sitting).`,
                  )
                ) {
                  lifecycle('Close', () => strategiesApi.close(strategy.id));
                }
              }}
              disabled={!canWrite || busy !== null}
              className={cn(busy === 'Close' && 'opacity-50')}
            >
              <Square className="h-3.5 w-3.5" />
              {busy === 'Close' ? '…' : 'Close'}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Create modal ────────────────────────────────────────────────────────

function CreateStrategyModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data: walletsData } = useWallets();
  const [form, setForm] = useReactState({
    slug: '',
    displayName: '',
    /** 0 = primary signer (no registry link). >0 = registered wallet id. */
    walletId: 0,
    walletAddress: '',
    /** DEX venue: aerodrome (Slipstream + AERO) or uniswap_v3 (fee income). */
    venue: 'aerodrome' as 'aerodrome' | 'uniswap_v3',
    /** Uniswap fee tier (3000 = 0.3%). Only sent for uniswap_v3. */
    poolFee: 3000,
    poolAddress: '',
    hlPerpCoin: 'ETH',
    rangePct: 3,
    // budgetUsdc is the Path-2 primary input. Sent to both the
    // strategy-create endpoint (as deployedCapitalUsd for back-compat)
    // and the deployment-create endpoint (as the actual budget).
    budgetUsdc: 0,
    driftThresholdBps: 500,
    hedgeCooldownSec: 1800,
    redeployCooldownSec: 3600,
    outOfRangeGraceSec: 900,
    harvestCooldownSec: 21600,
    minHarvestValueUsd: 2,
    hedgeSlippageBps: 100,
    harvestSlippageBps: 100,
    autoHedgeEnabled: true,
    autoRedeployEnabled: true,
    autoHarvestEnabled: true,
    /**
     * 'continuous' (default): legacy behavior. Hedge rebalances on
     * drift-threshold trigger. Many small perp trades inside an epoch.
     *
     * 'static': hedge opens once at epoch start, frozen until next
     * redeploy or upper-range exit. Zero in-epoch rebalances.
     */
    hedgeModel: 'continuous' as 'continuous' | 'static',
    // Atomic on-chain close→swap→mint via SwapAndMintZap. Also gated by the
    // global USE_ZAP_REDEPLOY env flag + ZAP_CONTRACT_ADDRESS; default off.
    useZapRedeploy: false,
    mode: 'live' as 'live' | 'paper',
  });
  const [submitting, setSubmitting] = useReactState(false);
  const [error, setError] = useReactState<string | null>(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      // Build the strategy-create payload. We send budgetUsdc as
      // deployedCapitalUsd for back-compat — the backend treats them
      // as equivalent. The Path-2 budget tracking happens via the
      // separate /deployments endpoint we call right after.
      const { budgetUsdc, walletId, walletAddress, poolFee, ...rest } = form;
      const payload: Record<string, unknown> = {
        ...rest,
        deployedCapitalUsd: budgetUsdc,
      };
      // Fee tier only applies to Uniswap pools (it must match poolAddress).
      if (form.venue === 'uniswap_v3') payload.poolFee = poolFee;
      // Wallet routing: prefer the registry picker (walletId). The backend
      // derives walletAddress from the wallet row. walletId=0 → primary
      // signer (send neither). A free-text override is kept only as a
      // fallback when no wallet is picked.
      if (walletId > 0) {
        payload.walletId = walletId;
      } else if (walletAddress.trim() !== '') {
        payload.walletAddress = walletAddress.trim().toLowerCase();
      }

      // 1. Create the strategy row.
      const created = await strategiesApi.create(payload as never);

      // 2. Kick off a dry-run deployment. The bot's main loop will pick
      //    it up within ~30s and walk the 6 steps. The user can review
      //    the plan in the Deployment Progress view, then click Execute.
      await deploymentsApi.create(created.id, {
        budgetUsdc,
        mode: 'dry_run',
      });

      await qc.invalidateQueries({ queryKey: ['strategies'] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function update<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm({ ...form, [k]: v });
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-border bg-surface shadow-xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">New strategy</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Creates the strategy + a dry-run deployment. The bot walks the
            allocation / swap / mint / stake steps in preview mode within ~30s.
            Review the plan in the Deployment Progress view, then click
            Execute to commit funds.
          </p>
        </div>
        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <FormSection title="Identity">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Slug" hint="lowercase, hyphenated">
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => update('slug', e.target.value.toLowerCase())}
                  placeholder="virtual-5pct"
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
              <Field label="Display name">
                <input
                  type="text"
                  value={form.displayName}
                  onChange={(e) => update('displayName', e.target.value)}
                  placeholder="VIRTUAL ±5%"
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
            </div>
            <Field
              label="Wallet"
              hint="Pick a registered wallet (Wallets page) or use the bot's primary signer. The wallet's key must be loaded in .env and show 'Key OK' health."
            >
              <select
                value={form.walletId}
                onChange={(e) => update('walletId', Number(e.target.value))}
                className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value={0}>Primary signer (default)</option>
                {(walletsData?.wallets ?? [])
                  .filter((w) => w.enabled)
                  .map((w) => (
                    <option key={w.id} value={w.id} disabled={w.health !== 'ok'}>
                      {w.label} — {w.address.slice(0, 6)}…{w.address.slice(-4)}
                      {w.health !== 'ok' ? ` (${w.health})` : ''}
                    </option>
                  ))}
              </select>
            </Field>
          </FormSection>
          <FormSection title="Position">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Venue"
                hint="Aerodrome: gauge staking, income = AERO emissions. Uniswap v3: no gauge, income = swap fees (position stays wallet-owned)."
              >
                <select
                  value={form.venue}
                  onChange={(e) =>
                    update('venue', e.target.value as 'aerodrome' | 'uniswap_v3')
                  }
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="aerodrome">Aerodrome Slipstream</option>
                  <option value="uniswap_v3">Uniswap v3</option>
                </select>
              </Field>
              {form.venue === 'uniswap_v3' && (
                <Field label="Fee tier" hint="Must match the pool address.">
                  <select
                    value={form.poolFee}
                    onChange={(e) => update('poolFee', Number(e.target.value))}
                    className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={100}>0.01% (100)</option>
                    <option value={500}>0.05% (500)</option>
                    <option value={3000}>0.30% (3000)</option>
                    <option value={10000}>1.00% (10000)</option>
                  </select>
                </Field>
              )}
            </div>
            <Field
              label={
                form.venue === 'uniswap_v3'
                  ? 'Uniswap v3 pool address'
                  : 'Aerodrome Slipstream pool address'
              }
            >
              <input
                type="text"
                value={form.poolAddress}
                onChange={(e) => update('poolAddress', e.target.value)}
                placeholder="0x..."
                className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="HL perp coin" hint="e.g. ETH, VIRTUAL — the perp shorted as the hedge">
                <input
                  type="text"
                  value={form.hlPerpCoin}
                  onChange={(e) => update('hlPerpCoin', e.target.value.toUpperCase())}
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
              <Field label="Range" suffix="%">
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="50"
                  value={form.rangePct}
                  onChange={(e) => update('rangePct', Number(e.target.value))}
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
              <Field label="Mode">
                <select
                  value={form.mode}
                  onChange={(e) => update('mode', e.target.value as 'live' | 'paper')}
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="live">live</option>
                  <option value="paper">paper</option>
                </select>
              </Field>
            </div>
          </FormSection>
          <FormSection title="Capital">
            <Field
              label="Budget"
              suffix="USDC"
              hint="USDC amount this strategy will deploy. Bot will split ~67% to LP, ~33% to HL. After creating, a DRY-RUN deployment runs first so you can review the plan before committing real funds."
            >
              <input
                type="number"
                step="10"
                min="50"
                value={form.budgetUsdc}
                onChange={(e) => update('budgetUsdc', Number(e.target.value))}
                className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </Field>
          </FormSection>
          <FormSection title="Thresholds">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Drift threshold" suffix="bps">
                <input
                  type="number"
                  step="50"
                  min="50"
                  value={form.driftThresholdBps}
                  onChange={(e) => update('driftThresholdBps', Number(e.target.value))}
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
              <Field label="Min harvest value" suffix="USD">
                <input
                  type="number"
                  step="0.5"
                  min="0.1"
                  value={form.minHarvestValueUsd}
                  onChange={(e) => update('minHarvestValueUsd', Number(e.target.value))}
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
              <Field label="Hedge cooldown" suffix="sec">
                <input
                  type="number"
                  min="60"
                  step="60"
                  value={form.hedgeCooldownSec}
                  onChange={(e) => update('hedgeCooldownSec', Number(e.target.value))}
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
              <Field label="Redeploy cooldown" suffix="sec">
                <input
                  type="number"
                  min="60"
                  step="60"
                  value={form.redeployCooldownSec}
                  onChange={(e) => update('redeployCooldownSec', Number(e.target.value))}
                  className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </Field>
            </div>
          </FormSection>
          <FormSection title="Feature flags">
            <div className="grid grid-cols-3 gap-3">
              <CheckBox
                label="Auto hedge"
                checked={form.autoHedgeEnabled}
                onChange={(v) => update('autoHedgeEnabled', v)}
              />
              <CheckBox
                label="Auto redeploy"
                checked={form.autoRedeployEnabled}
                onChange={(v) => update('autoRedeployEnabled', v)}
              />
              <CheckBox
                label="Auto harvest"
                checked={form.autoHarvestEnabled}
                onChange={(v) => update('autoHarvestEnabled', v)}
              />
            </div>
            <Field
              label="Hedge model"
              hint="continuous = drift-triggered rebalance (legacy). static = open once at epoch start, no in-epoch rebalances."
            >
              <select
                value={form.hedgeModel}
                onChange={(e) => update('hedgeModel', e.target.value as 'continuous' | 'static')}
                className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="continuous">continuous (track LP, many trades)</option>
                <option value="static">static (set once per epoch, then freeze)</option>
              </select>
            </Field>
            <CheckBox
              label="Use zap redeploy (atomic close→swap→mint)"
              checked={form.useZapRedeploy}
              onChange={(v) => update('useZapRedeploy', v)}
            />
          </FormSection>
          {error && (
            <div className="rounded-md border border-negative/40 bg-negative/10 p-2 text-xs text-negative">
              {error}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" size="md" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button size="md" onClick={submit} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create strategy'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {title}
      </h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({
  label,
  suffix,
  hint,
  children,
}: {
  label: string;
  suffix?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {suffix && (
          <span className="text-xs text-muted-foreground tabular-nums">{suffix}</span>
        )}
      </div>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </label>
  );
}

function CheckBox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-border"
      />
      <span className="text-foreground">{label}</span>
    </label>
  );
}
